import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import config from '../../Config';
import MaintenancePage from './MaintenancePage';
import LoadingScreen from './LoadingScreen';

const HealthCheckContext = createContext({
    isHealthy: true,
    lastCheck: null,
    checkNow: () => {}
});

export const useHealthCheck = () => useContext(HealthCheckContext);

const HEALTH_CHECK_INTERVAL = 15000; // 15 seconds
const HEALTH_CHECK_ENDPOINT = '/general/healthcheck';

export default function HealthCheckProvider({ children }) {
    const [isHealthy, setIsHealthy] = useState(true);
    const [lastCheck, setLastCheck] = useState(null);
    const [isInitialCheck, setIsInitialCheck] = useState(true);
    const intervalRef = useRef(null);
    const wasDownRef = useRef(false);
    /** После первого завершённого healthcheck (успех или ошибка) — для редиректа после восстановления API */
    const initialRoundDoneRef = useRef(false);

    const finishInitialRound = useCallback(() => {
        if (!initialRoundDoneRef.current) {
            initialRoundDoneRef.current = true;
            setIsInitialCheck(false);
        }
    }, []);

    const checkHealth = useCallback(async () => {
        try {
            const response = await axios.get(
                `${config.getApiBaseUrl()}${HEALTH_CHECK_ENDPOINT}`,
                {
                    timeout: 10000,
                    headers: {
                        apikey: config.apiKey
                    }
                }
            );

            const isOk = response.data?.is_ok === true;

            if (initialRoundDoneRef.current && wasDownRef.current && isOk) {
                const userToken = Cookies.get(config.authCookie);
                if (userToken) {
                    window.location.href = '/profile';
                } else {
                    window.location.href = '/auth';
                }
                return isOk;
            }

            if (!isOk) {
                wasDownRef.current = true;
            }

            setIsHealthy(isOk);
            setLastCheck(new Date());
            finishInitialRound();

            return isOk;
        } catch (error) {
            console.error('Health check failed:', error.message);
            const offline = typeof navigator !== 'undefined' && !navigator.onLine;
            if (offline) {
                wasDownRef.current = false;
                setIsHealthy(true);
            } else {
                wasDownRef.current = true;
                setIsHealthy(false);
            }
            setLastCheck(new Date());
            finishInitialRound();
            return false;
        }
    }, [finishInitialRound]);

    useEffect(() => {
        checkHealth();
        intervalRef.current = setInterval(checkHealth, HEALTH_CHECK_INTERVAL);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [checkHealth]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkHealth();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkHealth]);

    useEffect(() => {
        const onOffline = () => {
            wasDownRef.current = false;
            setIsHealthy(true);
        };
        const onOnline = () => {
            checkHealth();
        };
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);
        return () => {
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('online', onOnline);
        };
    }, [checkHealth]);

    const value = {
        isHealthy,
        lastCheck,
        checkNow: checkHealth
    };

    if (isInitialCheck) {
        return (
            <HealthCheckContext.Provider value={value}>
                <LoadingScreen />
            </HealthCheckContext.Provider>
        );
    }

    return (
        <HealthCheckContext.Provider value={value}>
            {isHealthy ? children : <MaintenancePage />}
        </HealthCheckContext.Provider>
    );
}
